import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  ChartOptions,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import styled from "styled-components";
import { AsistenciaPorDia } from "../../../services/analisisOperaciones";
import { useEstadisticasNumericas } from "../../../hooks/estadisticasNumericas";
import { formatearFechaCortaStr } from "../../../utils/dateUtils";
import { divertidogsTheme } from "../../../styles/divertidogs-theme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

interface AsistenciasPorDiasChartProps {
  asistenciasPorDia: AsistenciaPorDia[];
}

const formatearConteo = (valor: number) =>
  new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: Number.isInteger(valor) ? 0 : 1,
    maximumFractionDigits: Number.isInteger(valor) ? 0 : 1,
  }).format(valor);

function AsistenciasPorDiasChart({
  asistenciasPorDia,
}: AsistenciasPorDiasChartProps) {
  const mainColor = divertidogsTheme.colors.main;
  const promedioColor = divertidogsTheme.colors.info;
  const minimoColor = divertidogsTheme.colors.error;
  const maximoColor = divertidogsTheme.colors.success;
  const estadisticas = useEstadisticasNumericas(
    asistenciasPorDia,
    (item) => item.conteo
  );

  const chartData = useMemo(() => {
    const labels = asistenciasPorDia.map((item) => formatearFechaCortaStr(item.fecha));
    const conteos = asistenciasPorDia.map((item) => item.conteo);

    return {
      labels,
      datasets: [
        {
          label: "Asistencias",
          data: conteos,
          borderColor: mainColor,
          backgroundColor: `${mainColor}20`,
          fill: true,
          pointRadius: 5,
          pointBackgroundColor: mainColor,
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
        },
      ],
    };
  }, [asistenciasPorDia, mainColor]);

  const chartOptions: ChartOptions<"line"> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Asistencias diarias",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<"line">) {
            const valor = context.parsed.y;
            return typeof valor === "number"
              ? `${context.dataset.label}: ${formatearConteo(valor)}`
              : String(context.dataset.label ?? "");
          },
        },
      },
      annotation: estadisticas && asistenciasPorDia.length > 1
        ? {
            annotations: {
              promedio: {
                type: "line",
                yMin: estadisticas.promedio,
                yMax: estadisticas.promedio,
                borderColor: promedioColor,
                borderDash: [6, 6],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: promedioColor,
                  content: `Prom: ${formatearConteo(estadisticas.promedio)}`,
                },
              },
              minimo: {
                type: "line",
                yMin: estadisticas.minimo,
                yMax: estadisticas.minimo,
                borderColor: minimoColor,
                borderDash: [2, 4],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: minimoColor,
                  content: `Min: ${formatearConteo(estadisticas.minimo)}`,
                },
              },
              maximo: {
                type: "line",
                yMin: estadisticas.maximo,
                yMax: estadisticas.maximo,
                borderColor: maximoColor,
                borderDash: [2, 4],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: maximoColor,
                  content: `Max: ${formatearConteo(estadisticas.maximo)}`,
                },
              },
            },
          }
        : undefined,
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return formatearConteo(Number(value));
          },
        },
      },
    },
  }), [
    estadisticas,
    promedioColor,
    minimoColor,
    maximoColor,
    asistenciasPorDia.length,
  ]);

  return (
    <ChartContainer>
      <Line data={chartData} options={chartOptions} />
    </ChartContainer>
  );
}

const ChartContainer = styled.div`
  height: 100%;
  width: 100%;
`;

export default React.memo(AsistenciasPorDiasChart);
